import { useEffect, useState } from 'react';

import './ResetPassword.css';
import { parseUrl } from '../../services/ParseUrl';
import { Button } from 'src/components/ui/button';
import { AuthWrapper } from 'src/components/AuthWrapper/AuthWrapper';
import { Controller, useForm } from 'react-hook-form';
import Input from 'src/components/Input/Input';
import { useNotification } from 'src/hooks/useNotification';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { passwordConfirmScheme, passwordScheme } from 'src/utils/validation-schemes';
import { useResetPasswordConfirmMutation } from 'src/services/authSsoService';
import { PATH_AUTH_SPA } from 'src/constants/spa-routes';
import { useNavigate } from 'react-router-dom';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

const Title = () => {
  return (
    <div className={'flex flex-col gap-2.5 items-center justify-center'}>
      <div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="53"
          height="32"
          viewBox="0 0 53 32"
          fill="none"
        >
          <path
            d="M16.3688 -4.26488e-05C12.1871 -4.24677e-05 8.07498 1.67282 5.11808 4.65054C2.16117 7.62825 0.499999 11.6669 0.499999 15.878C0.499999 20.0892 2.16117 24.1278 5.11808 27.1055C8.07498 30.0832 12.1871 31.7561 16.3688 31.7561L16.3688 15.878L16.3688 -4.26488e-05Z"
            fill="#05050D"
          />
          <path
            d="M36.6299 0C45.3942 0.000141731 52.4989 7.10888 52.499 15.8779C52.499 24.6471 45.3943 31.7557 36.6299 31.7559C27.8654 31.7559 20.7598 24.6471 20.7598 15.8779C20.7599 7.10879 27.8654 0 36.6299 0ZM36.5898 9.37109C33.0046 9.37109 30.0977 12.2798 30.0977 15.8672C30.0977 19.4545 33.0046 22.3623 36.5898 22.3623C40.175 22.3623 43.081 19.4545 43.0811 15.8672C43.0811 12.2798 40.1751 9.37113 36.5898 9.37109Z"
            fill="#05050D"
          />
        </svg>
      </div>
      <div>
        <p className={'uppercase text-[1.375rem] text-black font-bold'}>New Password</p>
      </div>
    </div>
  );
};

const ResetPassword = () => {
  const navigate = useNavigate();

  const [token, setToken] = useState('');

  useEffect(() => {
    const queryParams = parseUrl(globalThis.location.href);
    setToken(queryParams.t);
  }, []);

  const notification = useNotification();

  const [resetPasswordConfirm, { isLoading: resetPasswordConfirmIsLoading }] =
    useResetPasswordConfirmMutation();

  useLoaderWhile(resetPasswordConfirmIsLoading);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      password: '',
      password2: '',
    },
    resolver: yupResolver(
      object({
        password: passwordScheme({
          required: true,
          min: 8,
          minMessage: 'Min. 8 characters',
        }).label('Password'),
        password2: passwordConfirmScheme({
          required: true,
          min: 8,
          minMessage: 'Min. 8 characters',
        }).label('Confirm password'),
      }),
    ),
  });

  const submitFunc = (value: { password: string; password2: string }) => {
    resetPasswordConfirm({
      password: value.password,
      password2: value.password2,
      token: token,
    }).then((resetPasswordConfirmResult) => {
      if (!('error' in resetPasswordConfirmResult)) {
        notification.success('Password reset successfully!');
        navigate(PATH_AUTH_SPA.ROOT);
      }
    });
  };

  return (
    <AuthWrapper title={<Title />} sliderHidden>
      <div
        className={
          'flex flex-col w-[450px] md:w-[534px] rounded-sm-5 border border-white bg-[#ffffff66] pt-[25px] px-[46px] pb-10'
        }
      >
        <form id="reset-password-form" onSubmit={handleSubmit(submitFunc)}>
          <div className={'input-container'}>
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  type={'password'}
                  label={'Password'}
                  placeholder={'••••••••••••••••••••••••••••'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <div className={'input-container'}>
            <Controller
              control={control}
              name="password2"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  type={'password'}
                  label={'Confirm Password'}
                  placeholder={'••••••••••••••••••••••••••••'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                />
              )}
            />
          </div>
        </form>

        <div className="flex flex-col mt-6 md:gap-4 gap-3 justify-center">
          <Button
            form="reset-password-form"
            disabled={!isDirty}
            type="submit"
            className="min-h-[50px] min-w-[125px] text-sm font-semibold shadow-[0px_2px_2px_0px_rgba(0,_0,_0,_0.1)]"
          >
            Set new password
          </Button>
        </div>
      </div>
    </AuthWrapper>
  );
};

export default ResetPassword;
